import { useLocalization } from '@lib/localization';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { type CustomFolderChildRow } from '@pages/Projects';
import { useAddFolderMutation, useUpdateFolderMutation, type IFolder } from '@store/api/folderEndpoints';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { folderIdOf } from './folderTree';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The folder to rename; without it the modal adds a folder to `currentFolder`. */
  folderItem?: CustomFolderChildRow | null;
  currentFolder?: IFolder | null;
}

const folderSchema = z.object({
  name: z.string()
    .min(1, 'Name must be at least 1 characters')
    .max(255, 'Folder name is too long')
    .regex(/^[^\\/:*?"<>|]+$/, 'Folder name cannot contain invalid characters (\\ / : * ? " < > |)'),
}).strict();

// Moving lives in MoveDialog; this modal only names a folder, new or existing.
const FolderModal: React.FC<FolderModalProps> = ({ isOpen, onClose, folderItem, currentFolder }) => {
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const { t } = useLocalization();

  const [addFolder, { isLoading: isAddFolderLoading }] = useAddFolderMutation();
  const [updateFolder, { isLoading: isUpdateFolderLoading }] = useUpdateFolderMutation();
  const isLoading = isAddFolderLoading || isUpdateFolderLoading;

  const isRenameForm = Boolean(folderItem);

  useEffect(() => {
    setName(folderItem?.name ?? '');
  }, [folderItem]);

  const handleSubmit = async () => {
    if (isLoading) return;

    const validation = folderSchema.safeParse({ name });

    if (!validation.success) {
      setNameError(validation.error.formErrors.fieldErrors.name?.[0] ?? null);
      return;
    }

    // A rename sends the folder's own parent back, which the backend reads as "not moved".
    const result = folderItem
      ? await updateFolder({
        _id: folderItem.id,
        name: validation.data.name,
        parentFolder: folderIdOf((folderItem.item as IFolder).parentFolder),
      })
      : await addFolder({ data: { name: validation.data.name, parentFolder: currentFolder?._id ?? null } });

    if (!result.error) handleClose();
  };

  const handleClose = () => {
    if (isLoading) return;

    setName('');
    setNameError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>{isRenameForm ? t('Folder_Rename') : t('Folder_Add')}</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}>
          <TextField
            autoFocus
            fullWidth
            label={t('Common_Name')}
            margin="normal"
            variant="outlined"
            placeholder="Enter name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
            error={!!nameError}
            helperText={nameError}
            disabled={isLoading}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <div>
            <Button
              onClick={handleClose} color="primary" sx={{ mr: '12px' }} startIcon={<ClearIcon />} disabled={isLoading}>
              {t('Common_Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              color="primary"
              startIcon={<CheckIcon />}
              disabled={isLoading}
            >
              {isRenameForm ? t('Common_Save') : t('Common_Add')}
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FolderModal;
